import { SiteSeo } from '@blocks/site-seo'
import { homeBreadcrumb } from '@config'
import { PageContext } from '@contexts/page'
import { PageLayoutTypeContext } from '@contexts/page-layout-type'
import { ContentType } from '@entries/content-types'
import ContentfulDeprecatedPageSeo from '@entries/contentful-deprecated-page-seo'
import ContentfulDeprecatedPageTemplate from '@entries/contentful-deprecated-page-template'
import { Breadcrumbs } from '@layouts/breadcrumbs/breadcrumbs-deprecated'
import { Content } from '@layouts/content'
import FootnoteList from '@layouts/footnote-list'
import { Navbar } from '@layouts/navbar/navbar'
import { Entry } from '@resolvers/entry'
import { Container } from '@ui/container'
import { GoUpButton } from '@ui/go-up-button'
import clsx from 'clsx'
import { graphql, type HeadFC, type PageProps, Slice } from 'gatsby'
import React, { type FC, type PropsWithChildren } from 'react'

import { isHiddenClassname } from '../../../plugins/gatsby-plugin-espace-client'

type BreadcrumbContainerProps = {
  isBreadcrumbVisible: boolean
}

const BreadcrumbContainer: FC<PropsWithChildren<BreadcrumbContainerProps>> = ({
  children,
  isBreadcrumbVisible,
}) => {
  if (!isBreadcrumbVisible) return <>{children}</>
  return <Container className="py-3">{children}</Container>
}

const Page: FC<PageProps<Queries.ContentfulDeprecatedPageQuery>> = ({
  data,
  pageContext: gatsbyPageContext,
}) => {
  const { page } = data || {}

  if (!page) {
    return null
  }

  const hasHero = page.template && Boolean(page.template !== 'picto')
  const isBreadcrumbVisible = !hasHero
  const PageLayoutTypeContextValue = {
    isLandingPage: false,
    isFaqPage: false,
  }

  const fixedBreadcrumbs = (page.breadcrumbs || []).map(
    ({ pageUrl, headline, breadcrumbsLabel }) => ({
      name: breadcrumbsLabel || headline?.text,
      href: pageUrl,
    })
  )
  const breadcrumbs = fixedBreadcrumbs
    ? [
        homeBreadcrumb,
        ...fixedBreadcrumbs,
        {
          name: page.breadcrumbsLabel || page.headline?.text,
          href: page.pageUrl,
        },
      ]
    : []

  const isBreadcrumbsEmpty = breadcrumbs.length === 0
  return (
    <PageContext.Provider value={data.page}>
      <PageLayoutTypeContext.Provider value={PageLayoutTypeContextValue}>
        <>
          <GoUpButton />
          <Navbar informationBanner={gatsbyPageContext.banner} />
          <div className={clsx(isHiddenClassname, 'justify-center')}>
            {!isBreadcrumbsEmpty && (
              <BreadcrumbContainer isBreadcrumbVisible={isBreadcrumbVisible}>
                <Breadcrumbs
                  breadcrumbs={breadcrumbs}
                  isVisible={isBreadcrumbVisible}
                />
              </BreadcrumbContainer>
            )}
          </div>
          <Content>
            <Entry
              content={data.page}
              mapping={{
                [ContentType.ContentfulDeprecatedPage]:
                  ContentfulDeprecatedPageTemplate,
              }}
            />
          </Content>
          <FootnoteList />
          <Slice alias="footer" />
        </>
      </PageLayoutTypeContext.Provider>
    </PageContext.Provider>
  )
}

export const Head: HeadFC<Queries.ContentfulDeprecatedPageQuery> = ({
  data: { page },
}) => (
  <>
    <SiteSeo />
    <Entry
      content={page}
      mapping={{
        [ContentType.ContentfulDeprecatedPage]: ContentfulDeprecatedPageSeo,
      }}
    />
  </>
)

export const query = graphql`
  query ContentfulDeprecatedPage($id: String!) {
    page: contentful3TUjMz7TxKWiQYygwIysig(id: { eq: $id }) {
      ...ContentfulDeprecatedPageSeo
      ...ContentfulDeprecatedPageTemplate
    }
  }
`

export default Page
